import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Check, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RentalAgreement({ bookingId, guestName, bookingDetails, onAccept, readOnly = false }) {
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreementContent, setAgreementContent] = useState(null);

  const fetchAgreementContent = async () => {
    try {
      const res = await axios.get(`${API}/rental-agreement`);
      setAgreementContent(res.data);
    } catch (e) {
      console.log("Using default agreement");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchAgreementContent(); }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Get booking details with fallbacks
  const details = bookingDetails || {};
  const arrivalDate = details.check_in || "TBD";
  const departureDate = details.check_out || "TBD";
  const baseGuestCount = details.guest_count || 0;
  const nightlyRate = details.nightly_rate || "$800-$900";
  const cleaningFee = details.cleaning_fee || "$350";
  const golfCartGasFee = details.golf_cart_gas_fee || 50;
  const securityDeposit = details.protection_amount || 400;
  const selectedAddons = details.selected_addons || [];
  const addonsTotal = details.addons_total || 0;
  
  // CALCULATE CORRECT TOTALS - taxes must include add-ons!
  const baseTotalBeforeTax = (details.total_price || 0) - (details.taxes || 0); // Remove old tax
  const taxableAmount = baseTotalBeforeTax + addonsTotal;
  const correctTax = Math.round(taxableAmount * 0.09 * 100) / 100; // 9% Maine tax
  const tripTotal = taxableAmount + correctTax; // Total WITHOUT security deposit
  const totalWithSecurityDeposit = tripTotal + securityDeposit;
  
  // Display values
  const tax = correctTax;
  const totalAmount = tripTotal; // Show trip total (without security deposit)
  
  // Check if early check-in or late check-out add-ons are selected
  const hasEarlyCheckin = selectedAddons.some(addon => 
    addon.name?.toLowerCase().includes('early check-in') || 
    addon.name?.toLowerCase().includes('early checkin') ||
    addon.type === 'early_checkin'
  );
  const hasLateCheckout = selectedAddons.some(addon => 
    addon.name?.toLowerCase().includes('late check-out') || 
    addon.name?.toLowerCase().includes('late checkout') ||
    addon.type === 'late_checkout'
  );
  
  // Set check-in/check-out times based on add-ons
  const checkInTime = hasEarlyCheckin ? "1:00 PM" : "4:00 PM";
  const checkOutTime = hasLateCheckout ? "1:00 PM" : "10:00 AM";
  
  // Calculate payment due dates based on arrival date
  const formatPaymentDate = (checkIn, monthsBeforeArrival) => {
    if (!checkIn || checkIn === "TBD") return "TBD";
    try {
      const arrival = new Date(checkIn);
      const dueDate = new Date(arrival);
      dueDate.setMonth(dueDate.getMonth() - monthsBeforeArrival);
      return dueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return "TBD";
    }
  };
  
  const secondPaymentDate = formatPaymentDate(arrivalDate, 2);
  const finalPaymentDate = formatPaymentDate(arrivalDate, 1);
  
  // Calculate extra guests from add-ons
  const extraGuestsAddon = selectedAddons.find(addon => 
    addon.name?.toLowerCase().includes('extra guest') || 
    addon.name?.toLowerCase().includes('additional guest')
  );
  const extraGuestCount = extraGuestsAddon?.quantity || 0;
  const totalGuestCount = baseGuestCount + extraGuestCount;

  const handleSubmit = async () => {
    if (!agreed) {
      toast.error("Please check the box to agree to the rental agreement");
      return;
    }
    if (!signature.trim()) {
      toast.error("Please sign with your full name");
      return;
    }
    if (signature.toLowerCase().trim() !== guestName.toLowerCase().trim()) {
      toast.error("Signature must match guest name");
      return;
    }

    // If bookingId is "pending", just validate and call onAccept
    if (bookingId === "pending") {
      toast.success("Agreement signed successfully!");
      onAccept(signature);
      return;
    }

    // Otherwise, save agreement to existing booking
    setLoading(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/agreement`, {
        signature: signature,
        agreed_to_rules: true,
        signed_at: new Date().toISOString(),
      });
      toast.success("Agreement signed successfully!");
      onAccept(signature);
    } catch (e) {
      toast.error("Error saving agreement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#134E4A] text-white flex items-center justify-center">
          <FileText size={24} />
        </div>
        <div>
          <h2 className="font-serif text-2xl text-[#1C1917]">Vacation Rental Agreement</h2>
          <p className="text-sm text-[#78716C]">{readOnly ? "For your reference" : "Please review and sign before payment"}</p>
        </div>
      </div>

      {/* Full Agreement */}
      <div className={`bg-stone-50 border border-stone-200 p-4 md:p-6 mb-6 ${readOnly ? "" : "max-h-[60vh] overflow-y-auto"}`}>
        <div className="prose prose-sm max-w-none text-[#1C1917]">
          
          {/* Header */}
          <div className="text-center mb-6 pb-4 border-b border-stone-200">
            <h3 className="font-serif text-xl mb-1">Chestnut Hill Inn</h3>
            <p className="text-sm text-[#78716C]">Vacation Rental Agreement – 2026</p>
          </div>

          {/* Property & Parties */}
          <div className="mb-6">
            <p className="text-sm"><strong>Property:</strong> 209 Beach Avenue, Long Island, Maine 04050</p>
            <p className="text-sm"><strong>Owner:</strong> William Arvidson and Emmalie Arvidson (&quot;Owner&quot;)</p>
            {!readOnly && <p className="text-sm"><strong>Guest:</strong> {guestName}</p>}
          </div>

          {/* Section 1: Rental Dates */}
          {!readOnly && (
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">1. Rental Dates</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li><strong>Check-in:</strong> {checkInTime} EDT on {arrivalDate}{hasEarlyCheckin && " (Early Check-in)"}</li>
              <li><strong>Check-out:</strong> {checkOutTime} EDT on {departureDate}{hasLateCheckout && " (Late Check-out)"}</li>
              <li><strong>Number of guests:</strong> {totalGuestCount || "TBD"}{extraGuestCount > 0 && ` (${baseGuestCount} base + ${extraGuestCount} additional approved guests)`}</li>
            </ul>
          </div>
          )}

          {/* Section 2: Rental Fees */}
          {!readOnly && (
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">2. Rental Fees</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li><strong>Nightly rate:</strong> {typeof nightlyRate === 'number' ? `$${nightlyRate}` : nightlyRate}</li>
              <li><strong>Cleaning fee:</strong> {typeof cleaningFee === 'number' ? `$${cleaningFee}` : cleaningFee}</li>
              <li><strong>Golf cart gas fee:</strong> ${golfCartGasFee}</li>
              <li><strong>Maine Lodging Tax (9%):</strong> {typeof tax === 'number' ? `$${tax.toFixed(2)}` : tax}</li>
              {selectedAddons.length > 0 && (
                <>
                  <li><strong>Add-ons:</strong></li>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    {selectedAddons.map((addon, index) => {
                      // Use pre-calculated total if available, otherwise calculate
                      const addonTotal = addon.total || (addon.price * (addon.quantity || 1) * (addon.nights || 1));
                      const showNights = addon.nights && addon.nights > 1;
                      return (
                        <li key={index}>
                          {addon.name}
                          {addon.quantity > 1 ? ` (x${addon.quantity})` : ''}
                          {showNights ? ` × ${addon.nights} nights` : ''}
                          : ${typeof addonTotal === 'number' ? addonTotal.toFixed(2) : addonTotal}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              <li><strong>Total rental amount:</strong> {typeof totalAmount === 'number' ? `$${totalAmount.toFixed(2)}` : totalAmount}{addonsTotal > 0 && ` (includes $${addonsTotal.toFixed(2)} in add-ons)`}</li>
              <li><strong>Security deposit:</strong> {typeof securityDeposit === 'number' ? `$${securityDeposit}` : securityDeposit} (refundable if no damage)</li>
            </ul>
          </div>
          )}

          {/* Section 3: Payment Policy */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">3. Payment Policy</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>A <strong>$1,000 non-refundable deposit</strong> is required via credit card to secure the reservation.</li>
              <li>This deposit is applied toward the total rental cost, not added on.</li>
              <li><strong>Remaining balance split into 2 payments via Zelle, Venmo, or ACH:</strong>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Second payment (50% of remaining): Due 2 months prior — <strong>{secondPaymentDate}</strong></li>
                  <li>Final payment (50% of remaining): Due 1 month prior — <strong>{finalPaymentDate}</strong></li>
                </ul>
              </li>
              <li><strong>Payment methods for remaining balance:</strong>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Zelle: chestnuthillinn@gmail.com</li>
                  <li>Venmo: @Emmalie-Arvidson</li>
                  <li>ACH: Contact us for bank details</li>
                </ul>
              </li>
              <li><strong>If booking within 2 months of arrival:</strong> Deposit + second payment due at booking</li>
              <li><strong>If booking within 1 month of arrival:</strong> Full payment due at booking</li>
              <li>A <strong>$500 refundable security deposit</strong> will be authorized 7-10 days before arrival and released after checkout inspection.</li>
              <li>Payment reminders will be sent 5 days prior to due dates.</li>
              <li><strong>If payment is not received within 10 days after the due date, the reservation will be cancelled and the $1,000 deposit will be forfeited.</strong></li>
            </ul>
          </div>

          {/* Section 4: Cancellation Policy */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">4. Cancellation Policy</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>The <strong>$1,000 deposit is non-refundable</strong> under all circumstances.</li>
              <li>Cancel &gt;60 days before check-in: Remaining payments (beyond the $1,000 deposit) are refundable.</li>
              <li>Cancel 30-60 days before check-in: 50% of remaining balance refundable.</li>
              <li>Cancel &lt;30 days before check-in: No refund of any payments.</li>
              <li>If we are able to re-rent the property for your cancelled dates, we may offer a partial refund at our discretion.</li>
              <li>Weather or health-related cancellations: Option to reschedule within current or next rental season (subject to availability).</li>
            </ul>
          </div>

          {/* Section 5: Golf Cart Use & Liability */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">5. Golf Cart Use & Liability</h4>
            <p className="text-sm mb-2">This rental includes complimentary use of one (1) 4-seater golf cart during your stay. By accepting this agreement, the Guest acknowledges and agrees to the following:</p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Use of the 4-seater golf cart is <strong>at the Guest&apos;s own risk</strong></li>
              <li>The Owners carry <strong>no liability coverage</strong> for the golf cart</li>
              <li>Guest assumes <strong>full responsibility</strong> for any damage, theft, loss, accidents, injury, or mechanical breakdowns occurring while the golf cart is in Guest&apos;s care</li>
              <li>The <strong>maximum speed limit on Long Island is 20 mph</strong> — Guest agrees to operate the golf cart safely and lawfully</li>
              <li>Guest is responsible for any fines, citations, or damages resulting from improper use</li>
              <li>A <strong>$50 gas fee</strong> is included in the booking total to refill the tank for the next guest</li>
            </ul>
          </div>

          {/* Section 6: Housekeeping */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">6. Housekeeping & Guest Responsibilities</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Guests responsible for basic cleaning (spills, sweeping)</li>
              <li>Strip sheets/towels and leave in designated laundry bins</li>
              <li>Excessive cleaning: $45/hour (1-hour minimum)</li>
              <li>Use kitchen entrance and rinse off sand at outdoor hose/platform</li>
              <li>Report breakage or accidental damage promptly</li>
            </ul>
          </div>

          {/* Section 7: Occupancy */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">7. Occupancy</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Maximum guests: 14 (additional guests over 14 require approval and must be added via Add-Ons)</li>
              <li>Parties/events require prior written approval</li>
              <li>The primary booking guest (renter) must be 25 years of age or older</li>
            </ul>
          </div>

          {/* Section 8: Conduct */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">8. Conduct</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>No disturbance to neighbors or illegal activity</li>
              <li>No subletting without Owner approval</li>
            </ul>
          </div>

          {/* Section 9: Pets & Smoking */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">9. Pets & Smoking</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Pets allowed only with prior approval ($150 fee per stay, max 1 pet)</li>
              <li>Smoking permitted outside only</li>
            </ul>
          </div>

          {/* Section 10: Add-ons */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">10. Add-ons / Extra Services</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Early check-in ($75) and late check-out ($75), if available</li>
              <li>Other add-ons: luggage pickup/delivery, kayak rental, extra pack-and-play, firewood, propane, extra guests ($20/person over 14)</li>
              <li>Availability may vary; system automatically prevents same-day check-in conflicts for cleaning</li>
            </ul>
          </div>

          {/* Section 11: Liability & Indemnification */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">11. Liability & Indemnification</h4>
            <div className="text-sm space-y-3">
              <p>The Renter agrees to assume full responsibility for the conduct of all occupants and guests during the rental period. The Renter accepts all risks associated with use of the property and its amenities.</p>
              <p>The Owner shall not be liable for any personal injury, illness, death, loss, theft, or damage to personal property belonging to the Renter or any member of the rental party.</p>
              <p>The Renter agrees to indemnify, defend, and hold harmless the Owner from and against any claims, damages, losses, liabilities, or expenses arising from the Renter&apos;s occupancy or use of the property, including but not limited to personal injury or property damage caused by the Renter or their guests.</p>
              <p>The security deposit does not limit the Renter&apos;s liability for damages, excessive cleaning, rule violations, or missing items discovered after departure.</p>
            </div>
          </div>

          {/* Section 12: Owner Rights */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">12. Owner Rights</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>The Owner reserves the right to enter the property if rental terms are violated.</li>
              <li>The Owner may cancel the reservation or evict guests for violation of house rules.</li>
              <li>Forfeited payments shall be considered liquidated damages.</li>
            </ul>
          </div>

          {/* Section 13: Calendar & Booking */}
          <div className="mb-5">
            <h4 className="font-semibold text-[#134E4A] mb-2">13. Calendar & Booking</h4>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Check-out is at 10:00 AM; check-in is at 4:00 PM.</li>
              <li>Automatic calendar blocks prevent same-day booking conflicts.</li>
              <li>Add-ons are only available when feasible.</li>
            </ul>
          </div>

          {/* Owner's Quarters Disclosure */}
          <div className="mb-5 bg-amber-50 border border-amber-200 p-4">
            <h4 className="font-semibold text-[#134E4A] mb-2">Owner&apos;s Quarters Disclosure</h4>
            <div className="text-sm space-y-2">
              <p>Chestnut Hill Inn is a two-family property. The rental unit and the owner&apos;s quarters are fully separate and include separate entrances, kitchens, and private living spaces.</p>
              <p>The owner&apos;s quarters are located at the rear of the property and may be occupied by the owners or their guests during your stay. Guests should expect that both units may be occupied, particularly during the summer months.</p>
              <p>While all spaces are private and independent, the Owner reserves the right to access and occupy their unit and shared exterior areas as needed.</p>
            </div>
          </div>

        </div>
      </div>

      {/* Agreement Acceptance Section */}
      {!readOnly && (
      <div className="space-y-4 border-t border-stone-200 pt-6">
        <h4 className="font-semibold text-[#134E4A]">13. Agreement Acceptance</h4>
        <p className="text-sm text-[#78716C]">
          By checking the box below, you agree to all terms of the Chestnut Hill Inn Vacation Rental Agreement and House Rules.
        </p>
        
        <div className="flex items-start gap-3 bg-[#e6f2f1] p-4">
          <Checkbox
            id="agree"
            checked={agreed}
            onCheckedChange={setAgreed}
            className="mt-1"
          />
          <label htmlFor="agree" className="text-sm text-[#1C1917] cursor-pointer font-medium">
            I have read and agree to the Chestnut Hill Inn Vacation Rental Agreement and House Rules.
          </label>
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#78716C] mb-2 block">
            Electronic Signature — <strong className="text-[#1C1917]">TYPE YOUR FULL NAME EXACTLY AS SHOWN</strong>
          </label>
          <Input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder={`Type: ${guestName}`}
            className="font-serif text-lg border-2 border-stone-300 focus:border-[#134E4A] p-4"
          />
          <p className="text-xs text-[#78716C] mt-1">
            Must match: <strong>{guestName}</strong>
          </p>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={loading || !agreed || !signature}
          className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-sm uppercase tracking-widest"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Signing...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Sign & Accept Agreement
            </>
          )}
        </Button>
      </div>
      )}
    </div>
  );
}
